
import { format } from "date-fns";

// Convert minutes since midnight to HH:MM format and AM/PM
export const minutesToTime = (minutes: number | null): { time: string; period: 'AM' | 'PM' } => {
  console.log("[TimePicker] Converting minutes to time:", minutes);
  
  if (minutes === null) return { time: '', period: 'AM' };
  
  try {
    // Direct calculation instead of using Date object to avoid timezone issues
    let hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    // Determine period (AM/PM)
    const period = hours >= 12 ? 'PM' : 'AM';
    
    // Convert to 12-hour format
    if (hours > 12) {
      hours -= 12;
    } else if (hours === 0) {
      hours = 12;
    }
    
    // Format the time string
    const timeString = `${hours}:${mins.toString().padStart(2, '0')}`;
    
    return { time: timeString, period };
  } catch (error) {
    console.error("[TimePicker] Error formatting time:", error);
    return { time: '', period: 'AM' };
  }
};

// Convert HH:MM and AM/PM to minutes since midnight
export const timeToMinutes = (time: string, period: 'AM' | 'PM'): number | null => {
  console.log("[TimePicker] Converting time to minutes:", { time, period });
  
  if (!time) return null;
  
  try {
    if (!time.includes(':')) {
      console.log("[TimePicker] Invalid time format - no colon");
      return null;
    }
    
    const [hoursStr, minutesStr] = time.split(':');
    let hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);
    
    if (isNaN(hours) || isNaN(minutes) || hours < 1 || hours > 12 || minutes < 0 || minutes > 59) {
      console.log("[TimePicker] Invalid time values");
      return null;
    }
    
    // Convert to 24-hour format
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    
    const totalMinutes = hours * 60 + minutes;
    console.log("[TimePicker] Converted to minutes:", totalMinutes);
    return totalMinutes;
  } catch (error) {
    console.error("[TimePicker] Error converting time to minutes:", error);
    return null;
  }
};

// Format user input for time - now only used on blur
export const formatTimeInput = (input: string): string => {
  if (!input) return '';
  
  // If only digits are entered without colon
  if (/^\d{1,4}$/.test(input)) {
    // Parse as hour and minutes
    if (input.length <= 2) {
      // Treat as just hours
      return `${input}:00`;
    } else {
      // Split into hours and minutes
      const hours = input.slice(0, input.length - 2);
      const minutes = input.slice(-2);
      return `${hours}:${minutes}`;
    }
  }
  
  return input;
};

// Normalize time values to valid ranges - only used for validation
export const normalizeTimeValues = (timeInput: string): string => {
  if (!timeInput.includes(':')) {
    // If there's no colon, treat the whole thing as hours
    const hours = parseInt(timeInput, 10) || 0;
    return `${Math.min(Math.max(hours, 1), 12)}:00`;
  }
  
  const parts = timeInput.split(':');
  let hours = parseInt(parts[0], 10) || 0;
  let minutes = parseInt(parts[1], 10) || 0;
  
  // Normalize values
  hours = Math.min(Math.max(hours, 1), 12);
  minutes = Math.min(Math.max(minutes, 0), 59);
  
  // Create the properly formatted time
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
};
